import React from 'react'
import { Button, Typography } from '@telavita-core/react-design-kit'

import ModalGeneric from 'containers/ModalManager/Modals/ModalGeneric'

import { ReactComponent as RecordingImage } from 'static/svg/auto-fill-guide-record-modal/recording.svg'
import { ReactComponent as DeleteImage } from 'static/svg/auto-fill-guide-record-modal/delete.svg'
import { ReactComponent as DocumentImage } from 'static/svg/auto-fill-guide-record-modal/document.svg'
import { ReactComponent as LockOpenImage } from 'static/svg/auto-fill-guide-record-modal/lock-open.svg'
import { ReactComponent as ChecklistImage } from 'static/svg/auto-fill-guide-record-modal/checklist.svg'

import * as S from './styles'

interface AutoFillGuideMedicalRecordModalProps {
  onClose: () => void
}

export function AutoFillGuideMedicalRecordModal({ onClose }: AutoFillGuideMedicalRecordModalProps): JSX.Element {
  return (
    <ModalGeneric
      hasTopBarActive={false}
      type='medium-large'
      modalTitle={
        <Typography
          center
          weight='bold'
          variant='header4'
        >
          Saiba mais sobre o preenchimento automático do prontuário
        </Typography>
      }
      onCloseModal={onClose}
    >
      <S.AutoFillGuideContainer>
        <Typography
          variant='content2'
          inlineStyles={{ lineHeight: '24px' }}
        >
          Sabemos que, no dia a dia de muitos atendimentos, 
          registrar todas as informações de cada paciente pode ser um desafio. 
          Por isso, combinamos a conveniência da tecnologia com um rigoroso processo de segurança de dados para promover o preenchimento automático do prontuário. 
          Assim, tornamos seu dia mais fácil, permitindo que você se concentre no que realmente importa: oferecer o melhor atendimento possível. Veja como este processo funciona:
        </Typography>

        <Guide />
        <ImportantDisclaimer />
        <Button variant='contained' uppercase={false} onClick={onClose}>
          Entendi
        </Button>
      </S.AutoFillGuideContainer>
    </ModalGeneric>
  )
}

const Guide = () => {
  return (
    <S.GuideContainer>
      <S.GuideItem>
        <RecordingImage />
        <S.GuideItemContent>
          <Typography weight='bold' variant="content1">
            1. Gravação temporária do áudio da consulta
          </Typography>
          <Typography variant="content2">
            A partir do áudio captado na consulta, nosso serviço de IA (Inteligência artificial) transcreve seu conteúdo em texto, de forma resumida.
          </Typography>
        </S.GuideItemContent>
      </S.GuideItem>

      <S.GuideItem>
        <DeleteImage />
        <S.GuideItemContent>
          <Typography weight='bold' variant="content1">
            2. Descarte do áudio gravado
          </Typography>
          <Typography variant="content2">
            Depois que você faz sua consulta, nosso sistema cuida de transcrever tudo automaticamente, de forma resumida. Assim que isso é feito, nós deletamos o áudio gravado para garantir que tudo fique super seguro. Ninguém, nem mesmo a equipe da Telavita, tem acesso a esses áudios. Eles são usados apenas para criar o resumo da sua consulta.
          </Typography>
        </S.GuideItemContent>
      </S.GuideItem>

      <S.GuideItem>
        <DocumentImage />
        <S.GuideItemContent>
          <Typography weight='bold' variant="content1">
            3. Pré-preenchimento do prontuário
          </Typography>
          <Typography variant="content2">
            A partir da transcrição em resumo, os formulários de avaliação, evolução e desfecho do prontuário são pré-preenchidos.
          </Typography>
        </S.GuideItemContent>
      </S.GuideItem>

      <S.GuideItem>
        <LockOpenImage />
        <S.GuideItemContent>
          <Typography weight='bold' variant="content1">
            4. Permissão requerida ao paciente
          </Typography>
          <Typography variant="content2">
            Levamos a LGPD (Lei geral de proteção de dados) muito a sério e, por isso, pedimos a permissão do paciente para gravar temporariamente o áudio das consultas. Se ele preferir não permitir, infelizmente o preenchimento automático será desativado. Em cada consulta, te avisaremos se o preenchimento automático está ativado ou não. Quando estiver desativado, será preciso anotar o conteúdo da consulta para preencher o prontuário manualmente.
          </Typography>
        </S.GuideItemContent>
      </S.GuideItem>

      <S.GuideItem>
        <ChecklistImage />
        <S.GuideItemContent>
          <Typography weight='bold' variant="content1">
            5. Revisão e aprovação do pré-preenchimento
          </Typography>
          <Typography variant="content2">
            Depois que você realizar uma consulta, a pendência de revisão e/ou preenchimento do prontuário será representada por uma bolinha vermelha no menu de cada consulta.
          </Typography>
          <Typography variant='content2' inlineStyles={{ marginTop: '20px' }}>
            Quando você se deparar com prontuários que já estão preenchidos automaticamente, basta dar uma olhada em cada uma das perguntas para garantir que tudo está correto e, se estiver, aprová-los antes de publicá-los. Se necessário, você pode fazer qualquer ajuste, seja editando, removendo ou adicionando informações.
          </Typography>
        </S.GuideItemContent>
      </S.GuideItem>
    </S.GuideContainer>
  )
}

const ImportantDisclaimer = () => {
  return (
    <S.ImportantDisclaimerContainer>
      <S.ImportantText>
        <Typography weight='bold' variant='header4'>
          Importante: <span>O processo de preenchimento automático do prontuário possui respaldo jurídico e está em conformidade com as regras da LGPD e CFP. Prezamos pelo sigilo e pela segurança das informações de todos os nossos pacientes.</span>
        </Typography>
      </S.ImportantText>

      <S.CFPLink 
        href='https://site.cfp.org.br/cfp-divulga-posicionamento-sobre-inteligencia-artificial-no-contexto-da-pratica-psicologica/' 
        target='_blank' 
        rel='noopener noreferrer'
      >
        <Typography color='primary' weight='bold' variant='content2'>
          Confira o posicionamento do CFP sobre Inteligência Artifical na prática psicológica.
        </Typography>
      </S.CFPLink>
    </S.ImportantDisclaimerContainer>
  )
}